<template>
  <div class="relative bg-gray-100 rounded-lg overflow-hidden shadow-lg">
    <div class="relative" :style="{ paddingBottom: aspectRatio }">
      <img
        ref="floorPlanImage"
        :src="planImageUrl"
        :alt="$t('floorPlan.imageAlt')"
        class="absolute inset-0 w-full h-full object-contain"
        @load="onImageLoad"
      />

      <!-- Interactive overlay areas for each room -->
      <svg
        v-if="imageDimensions.width > 0"
        class="absolute inset-0 w-full h-full"
        :viewBox="`0 0 ${imageDimensions.width} ${imageDimensions.height}`"
        preserveAspectRatio="xMidYMid meet"
      >
        <g v-for="room in displayRooms" :key="room.id">
          <polygon
            v-if="room.polygonPoints"
            :points="room.polygonPoints"
            :class="roomAreaClasses(room.id)"
            @click="$emit('room-selected', room.id)"
            @mouseenter="hoveredRoom = room.id"
            @mouseleave="hoveredRoom = null"
          />
          <rect
            v-else-if="room.rect"
            :x="room.rect.x"
            :y="room.rect.y"
            :width="room.rect.width"
            :height="room.rect.height"
            :class="roomAreaClasses(room.id)"
            @click="$emit('room-selected', room.id)"
            @mouseenter="hoveredRoom = room.id"
            @mouseleave="hoveredRoom = null"
          />
          <text
            v-if="room.labelX != null && room.labelY != null && room.labelText"
            :x="room.labelX"
            :y="room.labelY"
            :style="{ fontSize: room.labelFontSize }"
            class="pointer-events-none font-semibold fill-gray-800"
            text-anchor="middle"
            dominant-baseline="middle"
          >
            {{ room.labelText }}
          </text>
        </g>
      </svg>
    </div>

    <!-- Room list for mobile -->
    <div class="lg:hidden p-4 bg-white border-t">
      <h3 class="font-semibold mb-3 text-gray-800">{{ $t('floorPlan.selectRoomHeading') }}</h3>
      <div class="grid grid-cols-2 gap-2">
        <button
          v-for="room in rooms"
          :key="room.id"
          @click="$emit('room-selected', room.id)"
          :class="[
            'p-3 rounded-lg text-sm font-medium transition-colors',
            selectedRoomId === room.id
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
        >
          {{ room.label || room.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { resolvePropertyImageUrl } from '../../utils/propertyImageUrl.js';
import { normalizeRoomCoordinatesForViewer } from '@merlot/common/floorPlanCoordinates.js';
import defaults from '@merlot/common/propertySiteDefaults.json';

const props = defineProps({
  planImage: {
    type: String,
    required: true
  },
  rooms: {
    type: Array,
    required: true
  },
  selectedRoomId: {
    type: String,
    default: null
  },
  viewBox: {
    type: String,
    default: null
  },
  floorLabelFontSize: {
    type: String,
    default: ''
  }
});

defineEmits(['room-selected']);

const floorPlanImage = ref(null);
const hoveredRoom = ref(null);
const imageDimensions = ref({ width: 0, height: 0 });

const planImageUrl = computed(() => resolvePropertyImageUrl(props.planImage));

const displayRooms = computed(() => {
  return props.rooms.map((room) => {
    const norm = normalizeRoomCoordinatesForViewer(room.coordinates);
    const labelText = (room.label || room.name || '').trim();
    const fs =
      (room.labelFontSize || '').trim() ||
      (props.floorLabelFontSize || '').trim() ||
      defaults.labelFontSizeDefault;
    if (norm?.points) {
      return {
        id: room.id,
        polygonPoints: norm.points,
        labelX: norm.labelX,
        labelY: norm.labelY,
        labelText,
        labelFontSize: fs
      };
    }
    const c = room.coordinates;
    if (c && c.x != null && c.y != null && c.width != null && c.height != null) {
      return {
        id: room.id,
        rect: { x: c.x, y: c.y, width: c.width, height: c.height },
        labelX: norm?.labelX,
        labelY: norm?.labelY,
        labelText,
        labelFontSize: fs
      };
    }
    return { id: room.id, labelText, labelFontSize: fs };
  });
});

const aspectRatio = computed(() => {
  if (imageDimensions.value.width && imageDimensions.value.height) {
    return `${(imageDimensions.value.height / imageDimensions.value.width) * 100}%`;
  }
  return '75%';
});

const onImageLoad = () => {
  if (floorPlanImage.value) {
    imageDimensions.value = {
      width: floorPlanImage.value.naturalWidth || 1000,
      height: floorPlanImage.value.naturalHeight || 750
    };
  }
};

const roomAreaClasses = (roomId) => {
  const base = 'room-area cursor-pointer transition-all duration-200 stroke-none';
  if (props.selectedRoomId === roomId) {
    return `${base} fill-blue-500/20`;
  }
  if (hoveredRoom.value === roomId) {
    return `${base} fill-blue-400/15`;
  }
  return `${base} fill-blue-400/10`;
};
</script>

<style scoped>
.room-area {
  transition: all 0.2s ease;
}
</style>

